import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const TwoFASetup: React.FC = () => {
  const navigate = useNavigate();
  const { setup2FA, validate2FA, isLoading } = useAuth();
  
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [setupError, setSetupError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [qrCodeError, setQrCodeError] = useState<boolean>(false);

  useEffect(() => {
    // Fonction pour initialiser la configuration 2FA
    const initSetup = async () => {
      setQrCodeError(false);
      try {
        const result = await setup2FA();
        if (result && result.success && result.data) {
          if (result.data.secret) {
            setSecret(result.data.secret);
          }
          
          if (result.data.qrCodeUrl) {
            if (!result.data.qrCodeUrl.startsWith('data:') && !result.data.qrCodeUrl.startsWith('http')) {
              const formattedQrCode = `data:image/png;base64,${result.data.qrCodeUrl}`;
              setQrCodeUrl(formattedQrCode);
            } else {
              setQrCodeUrl(result.data.qrCodeUrl);
            }
          } else {
            // On ne met pas d'erreur car nous avons le secret et utiliserons le QR code de secours
          }
        } else {
          setQrCodeError(true);
        }
      } catch (error) {
        console.error('Exception lors de la récupération du QR code:', error);
        setQrCodeError(true);
      }
    };

    if (currentStep === 1) {
      initSetup();
    }
  }, [currentStep]);

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otpCode) {
      setSetupError('Veuillez entrer le code de vérification');
      return;
    }
    
    try {
      // Vérifier que nous avons bien un secret
      if (!secret) {
        setSetupError('Erreur: Secret 2FA manquant. Veuillez réinitialiser la configuration.');
        return;
      }
      
      // Appeler l'API de validation avec le code et le secret
      const result = await validate2FA({ 
        code: otpCode,
        secret: secret // Ajouter le secret pour la validation côté serveur
      });
      
      if (result.success) {
        // Passer à l'étape de confirmation
        setCurrentStep(3);
        setSetupError(null);
        console.log('2FA activé avec succès:', result.message);
      } else {
        // Afficher l'erreur spécifique retournée par l'API
        setSetupError(result.error || 'Code invalide. Veuillez réessayer.');
        console.error('Erreur de validation 2FA:', result.error);
      }
    } catch (error) {
      console.error('Erreur lors de la validation du code 2FA:', error);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Configuration de l'authentification à deux facteurs</h1>
        </div>

        {/* Indicateur de progression */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                1
              </div>
              <div className="ml-2 text-sm font-medium">
                Générer le QR code
              </div>
            </div>
            <div className={`flex-1 h-1 mx-4 ${currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                2
              </div>
              <div className="ml-2 text-sm font-medium">
                Vérifier le code
              </div>
            </div>
            <div className={`flex-1 h-1 mx-4 ${currentStep >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                3
              </div>
              <div className="ml-2 text-sm font-medium">
                Confirmation
              </div>
            </div>
          </div>
        </div>

        {/* Étape 1: Génération du QR code */}
        {currentStep === 1 && (
          <Card title="Étape 1: Scanner le QR code avec votre application d'authentification">
            <div className="space-y-6">
              <p className="text-sm text-gray-600">
                Scannez ce QR code avec votre application d'authentification (Google Authenticator, Authy, Microsoft Authenticator, etc.).
              </p>
              
              {isLoading && !qrCodeUrl && !secret ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : secret ? (
                <div className="flex flex-col items-center py-4">
                  {/* Affichage principal du QR code */}
                  {qrCodeUrl ? (
                    <>
                      <img 
                        src={qrCodeUrl} 
                        alt="QR Code pour 2FA" 
                        className="h-64 w-64 border border-gray-200 rounded-md mb-4" 
                        onLoad={() => console.log('QR code chargé avec succès')}
                        onError={(e) => {
                          console.error('Erreur de chargement du QR code:', e);
                          setQrCodeError(true);
                        }}
                      />
                    </>
                  ) : (
                    // QR code de secours généré par Google Charts API
                    <>
                      <p className="text-xs text-gray-500 mb-2">Utilisation du QR code de secours</p>
                      <img 
                        src={`https://chart.googleapis.com/chart?chs=200x200&cht=qr&choe=UTF-8&chl=otpauth://totp/API100:${encodeURIComponent(window.location.hostname)}?secret=${secret}&issuer=API100`} 
                        alt="QR Code de secours pour 2FA" 
                        className="h-64 w-64 border border-gray-200 rounded-md mb-4" 
                      />
                    </>
                  )}
                  
                  {/* Instructions et clé secrète */}
                  <div className="w-full max-w-md">
                    <p className="text-sm text-gray-600 mb-2">1. Scannez ce QR code avec votre application d'authentification</p>
                    <p className="text-sm text-gray-600 mb-4">2. Ou entrez manuellement cette clé secrète dans votre application :</p>
                    
                    <div className="bg-gray-100 p-3 rounded text-center font-mono text-sm break-all">
                      {secret}
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-2">Applications recommandées : Google Authenticator, Authy, Microsoft Authenticator</p>
                  </div>
                </div>
              ) : qrCodeError ? (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">Erreur lors de la génération du QR code. Veuillez réessayer.</p>
                      <button 
                        className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
                        onClick={() => setCurrentStep(1)}
                      >
                        Réessayer
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center py-4">
                  <p className="text-sm text-gray-600">Chargement du QR code...</p>
                </div>
              )}

              <div className="pt-6 flex justify-end">
                <Button
                  variant="primary"
                  onClick={nextStep}
                  disabled={!qrCodeUrl || !secret}
                >
                  Continuer
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Étape 2: Vérification du code */}
        {currentStep === 2 && (
          <Card title="Étape 2: Vérifier la configuration">
            <form onSubmit={handleValidate} className="space-y-6">
              <p className="text-sm text-gray-600">
                Entrez le code à 6 chiffres généré par votre application d'authentification pour vérifier la configuration.
              </p>
              
              <Input
                id="otpCode"
                label="Code de vérification"
                type="text"
                autoComplete="one-time-code"
                required
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                error={setupError || undefined}
                helperText="Entrez le code à 6 chiffres de votre application d'authentification"
              />
              
              <div className="pt-4 flex justify-between">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={prevStep}
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                >
                  Valider et activer
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Étape 3: Confirmation */}
        {currentStep === 3 && (
          <Card title="Étape 3: Configuration terminée">
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">Configuration réussie !</h3>
              <p className="mt-2 text-sm text-gray-500">
                L'authentification à deux facteurs est maintenant activée pour votre compte.
                Vous devrez fournir un code à 6 chiffres à chaque connexion.
              </p>
              <div className="mt-8">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  Retour au tableau de bord
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TwoFASetup;
